import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

// SemVer 2.0.0 grammar — https://semver.org/#backusnaur-form-grammar-for-valid-semver-versions
const SEMVER_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

const EXAMPLE_A = "1.4.2";
const EXAMPLE_B = "1.4.2-rc.1+build.42";

const parseSemver = (raw) => {
  if (raw == null) return { valid: false };
  const trimmed = String(raw).trim();
  if (!trimmed) return { valid: false, empty: true };
  // Tolerate a single leading "v" or "V" for parsing feedback, but flag it.
  const hadLeadingV = /^[vV]/.test(trimmed);
  const stripped = hadLeadingV ? trimmed.slice(1) : trimmed;
  const m = SEMVER_RE.exec(stripped);
  if (!m) return { valid: false, raw: trimmed, hadLeadingV };
  const [, major, minor, patch, prerelease, build] = m;
  return {
    valid: true,
    raw: trimmed,
    hadLeadingV,
    major,
    minor,
    patch,
    prerelease: prerelease || "",
    build: build || "",
    prereleaseIds: prerelease ? prerelease.split(".") : [],
    buildIds: build ? build.split(".") : [],
  };
};

const isNumericId = (s) => /^(0|[1-9]\d*)$/.test(s);

// Compare two prerelease identifier arrays per SemVer §11.
// Returns -1, 0, or 1 for the *identifier* arrays only.
const comparePrereleaseIds = (a, b) => {
  // A version with no prerelease has higher precedence than one that does.
  const aEmpty = a.length === 0;
  const bEmpty = b.length === 0;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const x = a[i];
    const y = b[i];
    if (x === y) continue;
    const xNum = isNumericId(x);
    const yNum = isNumericId(y);
    if (xNum && yNum) {
      const xn = BigInt(x);
      const yn = BigInt(y);
      if (xn < yn) return -1;
      if (xn > yn) return 1;
      continue;
    }
    if (xNum) return -1; // numeric identifiers have lower precedence
    if (yNum) return 1;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  if (a.length < b.length) return -1;
  if (a.length > b.length) return 1;
  return 0;
};

const cmpBigInt = (a, b) => {
  const ax = BigInt(a);
  const bx = BigInt(b);
  if (ax < bx) return -1;
  if (ax > bx) return 1;
  return 0;
};

const compareSemver = (a, b) => {
  const c1 = cmpBigInt(a.major, b.major);
  if (c1 !== 0) return { result: c1, driver: "major" };
  const c2 = cmpBigInt(a.minor, b.minor);
  if (c2 !== 0) return { result: c2, driver: "minor" };
  const c3 = cmpBigInt(a.patch, b.patch);
  if (c3 !== 0) return { result: c3, driver: "patch" };
  const c4 = comparePrereleaseIds(a.prereleaseIds, b.prereleaseIds);
  if (c4 !== 0) return { result: c4, driver: "prerelease" };
  return { result: 0, driver: "equal" };
};

const SemverComparator = () => {
  const { t } = useTranslation();
  const [a, setA] = useState(EXAMPLE_A);
  const [b, setB] = useState(EXAMPLE_B);

  const parsedA = useMemo(() => parseSemver(a), [a]);
  const parsedB = useMemo(() => parseSemver(b), [b]);

  const comparison = useMemo(() => {
    if (!parsedA.valid || !parsedB.valid) return null;
    return compareSemver(parsedA, parsedB);
  }, [parsedA, parsedB]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const swap = () => {
    setA(b);
    setB(a);
  };

  const loadExample = () => {
    setA(EXAMPLE_A);
    setB(EXAMPLE_B);
  };

  const clearAll = () => {
    setA("");
    setB("");
  };

  const renderParsed = (parsed, side) => {
    if (parsed.empty) {
      return (
        <p className="text-sm text-gray-500 italic">
          {t("semver_cmp_empty_hint")}
        </p>
      );
    }
    if (!parsed.valid) {
      return (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm"
        >
          {parsed.hadLeadingV
            ? t("semver_cmp_invalid_leading_v")
            : t("semver_cmp_invalid_format")}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2">
          <div className="text-xs uppercase tracking-wide text-indigo-700">
            {t("semver_cmp_field_major")}
          </div>
          <div className="font-mono">{parsed.major}</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2">
          <div className="text-xs uppercase tracking-wide text-indigo-700">
            {t("semver_cmp_field_minor")}
          </div>
          <div className="font-mono">{parsed.minor}</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2">
          <div className="text-xs uppercase tracking-wide text-indigo-700">
            {t("semver_cmp_field_patch")}
          </div>
          <div className="font-mono">{parsed.patch}</div>
        </div>
        <div className="col-span-3 bg-gray-50 border border-gray-200 rounded-md p-2">
          <div className="text-xs uppercase tracking-wide text-gray-600">
            {t("semver_cmp_field_prerelease")}
          </div>
          <div className="font-mono break-all">
            {parsed.prerelease || (
              <span className="text-gray-400 italic">
                {t("semver_cmp_field_none")}
              </span>
            )}
          </div>
        </div>
        <div className="col-span-3 bg-gray-50 border border-gray-200 rounded-md p-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide text-gray-600">
              {t("semver_cmp_field_build")}
            </div>
            <span className="text-xs text-gray-500">
              {t("semver_cmp_build_ignored")}
            </span>
          </div>
          <div className="font-mono break-all">
            {parsed.build || (
              <span className="text-gray-400 italic">
                {t("semver_cmp_field_none")}
              </span>
            )}
          </div>
        </div>
        <div className="col-span-3 flex justify-end">
          <StandardButton
            onClick={() =>
              copy(
                parsed.raw,
                side === "A" ? t("semver_cmp_version_a") : t("semver_cmp_version_b")
              )
            }
            type="secondary"
            variant="outlined"
            size="small"
          >
            {t("copy_button")}
          </StandardButton>
        </div>
      </div>
    );
  };

  const verdictKey = (() => {
    if (!comparison) return null;
    if (comparison.result < 0) return "semver_cmp_a_less";
    if (comparison.result > 0) return "semver_cmp_a_greater";
    return "semver_cmp_equal";
  })();

  const driverKey = comparison
    ? {
        major: "semver_cmp_driver_major",
        minor: "semver_cmp_driver_minor",
        patch: "semver_cmp_driver_patch",
        prerelease: "semver_cmp_driver_prerelease",
        equal: "semver_cmp_driver_equal",
      }[comparison.driver]
    : null;

  const verdictClass = (() => {
    if (!comparison) return "";
    if (comparison.result === 0) return "bg-gray-50 border-gray-300 text-gray-800";
    return "bg-green-50 border-green-300 text-green-900";
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("semver_cmp_title")}
            </h1>
            <p className="text-gray-600">{t("semver_cmp_intro")}</p>

            <div className="flex flex-wrap gap-2">
              <StandardButton
                onClick={loadExample}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("semver_cmp_load_example")}
              </StandardButton>
              <StandardButton
                onClick={swap}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("semver_cmp_swap")}
              </StandardButton>
              <StandardButton
                onClick={clearAll}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("semver_cmp_clear")}
              </StandardButton>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="semver-a"
                  className="text-sm font-medium text-gray-700"
                >
                  {t("semver_cmp_version_a")}
                </label>
                <input
                  id="semver-a"
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  placeholder={t("semver_cmp_input_placeholder")}
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  aria-invalid={
                    a && !parsedA.valid && !parsedA.empty ? "true" : "false"
                  }
                />
                {renderParsed(parsedA, "A")}
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="semver-b"
                  className="text-sm font-medium text-gray-700"
                >
                  {t("semver_cmp_version_b")}
                </label>
                <input
                  id="semver-b"
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  placeholder={t("semver_cmp_input_placeholder")}
                  value={b}
                  onChange={(e) => setB(e.target.value)}
                  aria-invalid={
                    b && !parsedB.valid && !parsedB.empty ? "true" : "false"
                  }
                />
                {renderParsed(parsedB, "B")}
              </div>
            </div>

            {comparison && (
              <div
                role="status"
                aria-live="polite"
                className={`border p-4 rounded-md ${verdictClass}`}
              >
                <div className="font-semibold text-lg">
                  {t(verdictKey, { a: parsedA.raw, b: parsedB.raw })}
                </div>
                <div className="text-sm opacity-80 mt-1">
                  {t(driverKey)}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 mt-2 text-xs text-gray-500 leading-relaxed">
              <p>{t("semver_cmp_note_spec")}</p>
              <p>{t("semver_cmp_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default SemverComparator;
